import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import '../../../../styles/dutyRoster.css';

function cleanText(value) {
  return String(value || '').trim();
}

function isGenericMemberName(value) {
  const name = cleanText(value).toLowerCase();
  return !name || name === 'member';
}

function displayNameFromProfile(profile = {}) {
  return cleanText(
    profile.name ||
    profile.displayName ||
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.username
  );
}

function toDate(value) {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function eventStartDate(event = {}) {
  const explicit = toDate(event.start);
  if (explicit) return explicit;
  if (event.date) {
    const parsed = new Date(`${event.date}T${event.time || '00:00'}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDateTime(event = {}) {
  const start = eventStartDate(event);
  if (!start) return 'Date TBC';
  return start.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Date TBC';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function normalizeTags(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === 'object') {
    return Object.entries(value).filter(([, enabled]) => Boolean(enabled)).map(([name]) => name);
  }
  return [];
}

function hasTagOverlap(allowedTags = [], memberTags = []) {
  const allowed = normalizeTags(allowedTags);
  if (allowed.length === 0) return true;
  const mine = new Set(normalizeTags(memberTags));
  return allowed.some((tagName) => mine.has(tagName));
}

function dutyRoles(event = {}) {
  return (Array.isArray(event.roles) ? event.roles : []).filter((role) => {
    if (role?.dutyRoster === true) return true;
    return (Array.isArray(role?.slots) ? role.slots : []).some((slot) => slot?.dutyRoster === true || slot?.assignedByRoster === true);
  });
}

function summarizeEventDuties(event = {}) {
  return dutyRoles(event).reduce(
    (acc, role) => {
      (Array.isArray(role?.slots) ? role.slots : []).forEach((slot) => {
        acc.total += 1;
        if (slot?.assigneeUserId) acc.filled += 1;
        if (!slot?.assigneeUserId || slot?.responseStatus === 'declined') acc.warning += 1;
        if (slot?.responseStatus === 'pending') acc.pending += 1;
      });
      return acc;
    },
    { total: 0, filled: 0, warning: 0, pending: 0 }
  );
}

function statusLabel(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'accepted') return 'Accepted';
  if (normalized === 'declined') return 'Declined';
  if (normalized === 'pending') return 'Needs response';
  return 'Assigned';
}

function swapStatusLabel(status) {
  const normalized = String(status || 'open').toLowerCase();
  if (normalized === 'pendingadmin') return 'Awaiting admin approval';
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'rejected') return 'Rejected';
  if (normalized === 'cancelled') return 'Cancelled';
  return 'Open';
}

function dutyCredit(role, slot) {
  const slotCredit = Number(slot?.dutyCredit);
  if (Number.isFinite(slotCredit) && slotCredit >= 0) return slotCredit;
  const roleCredit = Number(role?.dutyCredit);
  return Number.isFinite(roleCredit) && roleCredit >= 0 ? roleCredit : 1;
}

function formatDutyCredit(value) {
  const credit = Number(value);
  if (!Number.isFinite(credit)) return '1 credit';
  const label = credit % 1 === 0 ? credit.toFixed(0) : credit.toFixed(1);
  return `${label} ${credit === 1 ? 'credit' : 'credits'}`;
}

function currentYearSeason() {
  return deriveDutySeason('01-01');
}

function parseSeasonStartsOn(value) {
  const match = String(value || '').match(/^(\d{2})-(\d{2})$/);
  if (!match) return '01-01';
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (!Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12) return '01-01';
  const maxDay = new Date(2001, month, 0).getDate();
  if (day < 1 || day > maxDay) return '01-01';
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatLocalDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function deriveDutySeason(seasonStartsOn = '01-01', now = new Date()) {
  const startsOn = parseSeasonStartsOn(seasonStartsOn);
  const [month, day] = startsOn.split('-').map(Number);
  const year = now.getFullYear();
  let start = new Date(year, month - 1, day);
  if (now < start) start = new Date(year - 1, month - 1, day);
  const nextStart = new Date(start.getFullYear() + 1, month - 1, day);
  const end = new Date(nextStart.getTime() - 1);
  const sameYear = start.getFullYear() === end.getFullYear();
  return {
    name: sameYear
      ? `${start.getFullYear()} duty season`
      : `${start.getFullYear()}/${String(end.getFullYear()).slice(-2)} duty season`,
    seasonStartsOn: startsOn,
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
  };
}

function createTemplateRole(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    name: 'Race Officer',
    slots: 1,
    dutyCredit: 1,
    allowedTags: [],
    requiresApproval: true,
    ...overrides,
  };
}

function positiveInteger(value, fallback = 1) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : fallback;
}

function nonNegativeNumber(value, fallback = 1) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : fallback;
}

const CREDIT_BALANCE_LIMIT = 12;

export default function EditDutyRosterForm({
  isOpen,
  teamId,
  team = {},
  userId = '',
  isAdmin = false,
  initialTab = '',
  teamTags = [],
  memberProfiles = [],
  onClose,
}) {
  const [tab, setTab] = useState('overview');
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [season, setSeason] = useState(currentYearSeason);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [occurrenceByEvent, setOccurrenceByEvent] = useState({});
  const [draftId, setDraftId] = useState('');
  const [draftSlots, setDraftSlots] = useState([]);
  const [draftRsvpMode, setDraftRsvpMode] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [expandedRosterEventIds, setExpandedRosterEventIds] = useState([]);
  const [swapCreditOverrides, setSwapCreditOverrides] = useState({});
  const [creditBalanceSearch, setCreditBalanceSearch] = useState('');
  const [showAllCreditBalance, setShowAllCreditBalance] = useState(false);
  const [swapMemberNames, setSwapMemberNames] = useState({});
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    roles: [createTemplateRole()],
  });
  const [swapApprovalRequired, setSwapApprovalRequired] = useState(
    team?.modules?.sailing?.dutyRoster?.requiresAdminApprovalForSwaps !== false
  );

  const applyTemplateFn = useMemo(() => httpsCallable(functions, 'applyDutyRosterTemplate'), []);
  const createDraftFn = useMemo(() => httpsCallable(functions, 'createDutyRosterDraft'), []);
  const publishDraftFn = useMemo(() => httpsCallable(functions, 'publishDutyRosterDraft'), []);
  const respondDutyFn = useMemo(() => httpsCallable(functions, 'respondDutyAssignment'), []);
  const createSwapFn = useMemo(() => httpsCallable(functions, 'createDutySwapRequest'), []);
  const resolveSwapFn = useMemo(() => httpsCallable(functions, 'resolveDutySwapRequest'), []);
  const saveSettingsFn = useMemo(() => httpsCallable(functions, 'saveDutyRosterSettings'), []);

  const members = useMemo(
    () => (memberProfiles || []).map((member) => ({
      id: member.id,
      name: displayNameFromProfile(member) || 'Member',
      tags: normalizeTags(member.tags),
    })),
    [memberProfiles]
  );

  const memberNameById = useMemo(
    () => new Map(members.map((member) => [member.id, member.name])),
    [members]
  );

  const tagNames = useMemo(
    () => (Array.isArray(teamTags) ? teamTags.filter((tag) => tag?.name).map((tag) => tag.name) : []),
    [teamTags]
  );
  const seasonStartParts = useMemo(() => {
    const startsOn = parseSeasonStartsOn(season.seasonStartsOn);
    const [month, day] = startsOn.split('-');
    return { month, day };
  }, [season.seasonStartsOn]);
  const seasonStartDayCount = useMemo(
    () => new Date(2001, Number(seasonStartParts.month), 0).getDate(),
    [seasonStartParts.month]
  );
  const setSeasonStartPart = (part, value) => {
    const current = parseSeasonStartsOn(season.seasonStartsOn);
    const [currentMonth, currentDay] = current.split('-').map(Number);
    const nextMonth = part === 'month' ? Number(value) : currentMonth;
    const maxDay = new Date(2001, nextMonth, 0).getDate();
    const nextDay = Math.min(part === 'day' ? Number(value) : currentDay, maxDay);
    setSeason(deriveDutySeason(`${String(nextMonth).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`));
  };

  useEffect(() => {
    if (isOpen) setTab(initialTab || 'overview');
  }, [initialTab, isAdmin, isOpen]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    const unsubEvents = safeOnSnapshot(
      collection(db, 'teams', teamId, 'events'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((event) => eventStartDate(event))
          .sort((left, right) => eventStartDate(left) - eventStartDate(right));
        setEvents(rows);
      },
      undefined,
      { label: 'duty-roster-events', teamId }
    );
    const unsubTemplates = isAdmin
      ? safeOnSnapshot(
          collection(db, 'teams', teamId, 'dutyRosterTemplates'),
          (snapshot) => {
            const rows = snapshot.docs
              .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
              .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
            setTemplates(rows);
            setTemplateId((current) => current || rows[0]?.id || '');
          },
          undefined,
          { label: 'duty-roster-templates', teamId }
        )
      : () => {};
    const unsubSwaps = safeOnSnapshot(
      collection(db, 'teams', teamId, 'dutySwapRequests'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((left, right) => (right.updatedAt?.toMillis?.() || 0) - (left.updatedAt?.toMillis?.() || 0));
        setSwapRequests(rows);
      },
      undefined,
      { label: 'duty-roster-swaps', teamId }
    );
    getDoc(doc(db, 'teams', teamId, 'dutyRosterSeasons', 'current'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          const startsOn = data.seasonStartsOn || (typeof data.startDate === 'string' ? data.startDate.slice(5) : '01-01');
          setSeason({ ...data, ...deriveDutySeason(startsOn) });
        }
      })
      .catch(() => {});
    return () => {
      unsubEvents();
      unsubTemplates();
      unsubSwaps();
    };
  }, [isAdmin, isOpen, teamId]);

  useEffect(() => {
    setSwapApprovalRequired(team?.modules?.sailing?.dutyRoster?.requiresAdminApprovalForSwaps !== false);
  }, [team?.modules?.sailing?.dutyRoster?.requiresAdminApprovalForSwaps]);

  useEffect(() => {
    if (!isOpen || !teamId || swapRequests.length === 0) return undefined;
    const ids = new Set();
    swapRequests.forEach((swap) => {
      if (swap.requestedBy && isGenericMemberName(swap.requestedByName) && !swapMemberNames[swap.requestedBy]) {
        ids.add(swap.requestedBy);
      }
      if (swap.acceptingMemberId && isGenericMemberName(swap.acceptingMemberName) && !swapMemberNames[swap.acceptingMemberId]) {
        ids.add(swap.acceptingMemberId);
      }
    });
    if (ids.size === 0) return undefined;
    let cancelled = false;
    Promise.all(Array.from(ids).map(async (memberId) => {
      const localName = memberNameById.get(memberId);
      if (!isGenericMemberName(localName)) return [memberId, localName];
      const [memberSnap, userSnap] = await Promise.all([
        getDoc(doc(db, 'teams', teamId, 'members', memberId)).catch(() => null),
        getDoc(doc(db, 'users', memberId)).catch(() => null),
      ]);
      const name = displayNameFromProfile({
        ...(memberSnap?.exists?.() ? memberSnap.data() : {}),
        ...(userSnap?.exists?.() ? userSnap.data() : {}),
      });
      return [memberId, isGenericMemberName(name) ? '' : name];
    })).then((rows) => {
      if (cancelled) return;
      const nextNames = Object.fromEntries(rows.filter(([, name]) => name));
      if (Object.keys(nextNames).length > 0) {
        setSwapMemberNames((prev) => ({ ...prev, ...nextNames }));
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, memberNameById, swapMemberNames, swapRequests, teamId]);

  const futureEvents = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return events.filter((event) => {
      const start = eventStartDate(event);
      return start && start >= yesterday;
    });
  }, [events]);

  const myDuties = useMemo(() => {
    const rows = [];
    events.forEach((event) => {
      dutyRoles(event).forEach((role) => {
        (Array.isArray(role.slots) ? role.slots : []).forEach((slot) => {
          if (slot?.assigneeUserId !== userId) return;
          rows.push({ event, role, slot });
        });
      });
    });
    return rows.sort((left, right) => eventStartDate(left.event) - eventStartDate(right.event));
  }, [events, userId]);

  const seasonDutyStats = useMemo(() => {
    const start = Date.parse(`${season.startDate || currentYearSeason().startDate}T00:00:00`);
    const end = Date.parse(`${season.endDate || currentYearSeason().endDate}T23:59:59.999`);
    const now = Date.now();
    const stats = {};
    const ensureStats = (memberId) => {
      if (!stats[memberId]) {
        stats[memberId] = {
          memberId,
          completedCredits: 0,
          completedRows: [],
          futureDuties: 0,
          lastCompletedMillis: 0,
          lastCompletedLabel: 'None',
        };
      }
      return stats[memberId];
    };
    events.forEach((event) => {
      const startDate = eventStartDate(event);
      const millis = startDate?.getTime?.() || 0;
      if (!millis) return;
      dutyRoles(event).forEach((role) => {
        (Array.isArray(role.slots) ? role.slots : []).forEach((slot) => {
          if (!slot?.assigneeUserId || slot?.responseStatus === 'declined') return;
          const memberStats = ensureStats(slot.assigneeUserId);
          if (millis >= now) {
            memberStats.futureDuties += 1;
            return;
          }
          if (millis < start || millis > end) return;
          const credit = dutyCredit(role, slot);
          memberStats.completedCredits += credit;
          memberStats.completedRows.push({ event, role, slot, credit, millis });
          if (millis > memberStats.lastCompletedMillis) {
            memberStats.lastCompletedMillis = millis;
            memberStats.lastCompletedLabel = formatDateOnly(startDate);
          }
        });
      });
    });
    Object.values(stats).forEach((entry) => {
      entry.completedRows.sort((left, right) => right.millis - left.millis);
    });
    return stats;
  }, [events, season.endDate, season.startDate]);

  const seasonDutyCounts = useMemo(
    () => Object.fromEntries(Object.entries(seasonDutyStats).map(([memberId, stats]) => [memberId, stats.completedCredits])),
    [seasonDutyStats]
  );

  const mySeasonStats = seasonDutyStats[userId] || {
    completedCredits: 0,
    completedRows: [],
    futureDuties: 0,
    lastCompletedLabel: 'None',
  };

  const seasonCreditBalance = useMemo(
    () => members
      .map((member) => {
        const stats = seasonDutyStats[member.id] || {};
        return {
          memberId: member.id,
          name: member.name || 'Member',
          completedCredits: stats.completedCredits || 0,
          futureDuties: stats.futureDuties || 0,
          lastCompletedLabel: stats.lastCompletedLabel || 'None',
          lastCompletedMillis: stats.lastCompletedMillis || 0,
        };
      })
      .sort((left, right) =>
        left.completedCredits - right.completedCredits ||
        left.futureDuties - right.futureDuties ||
        left.lastCompletedMillis - right.lastCompletedMillis ||
        left.name.localeCompare(right.name)
      ),
    [members, seasonDutyStats]
  );

  const filteredCreditBalance = useMemo(() => {
    const query = cleanText(creditBalanceSearch).toLowerCase();
    if (!query) return seasonCreditBalance;
    return seasonCreditBalance.filter((entry) => entry.name.toLowerCase().includes(query));
  }, [creditBalanceSearch, seasonCreditBalance]);

  const visibleCreditBalance = useMemo(
    () => (showAllCreditBalance || creditBalanceSearch
      ? filteredCreditBalance
      : filteredCreditBalance.slice(0, CREDIT_BALANCE_LIMIT)),
    [creditBalanceSearch, filteredCreditBalance, showAllCreditBalance]
  );

  const hiddenCreditBalanceCount = Math.max(0, filteredCreditBalance.length - visibleCreditBalance.length);

  const toggleSelectedEvent = (eventId) => {
    setSelectedEventIds((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  const toggleExpandedRosterEvent = (eventId) => {
    setExpandedRosterEventIds((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId]
    );
  };

  const updateDutySlot = async ({ event, roleId, slotId, patch }) => {
    const roles = (Array.isArray(event.roles) ? event.roles : []).map((role) => {
      if (role.id !== roleId) return role;
      return {
        ...role,
        slots: (Array.isArray(role.slots) ? role.slots : []).map((slot) =>
          slot.id === slotId ? { ...slot, ...patch, dutyRoster: true } : slot
        ),
      };
    });
    setBusy(true);
    setError('');
    try {
      await setDoc(doc(db, 'teams', teamId, 'events', event.id), {
        roles,
        updatedAt: new Date(),
        updatedBy: userId,
      }, { merge: true });
    } catch (err) {
      setError(err?.message || 'Unable to update duty slot.');
    } finally {
      setBusy(false);
    }
  };

  const assignDutySlot = ({ event, role, slot, memberId }) => {
    const member = members.find((candidate) => candidate.id === memberId);
    updateDutySlot({
      event,
      roleId: role.id,
      slotId: slot.id,
      patch: member ? {
        assigneeUserId: member.id,
        assigneeDisplayName: member.name,
        responseStatus: 'pending',
        assignedByRoster: true,
        assignedAt: new Date(),
        assignedBy: userId,
      } : {
        assigneeUserId: null,
        assigneeDisplayName: '',
        responseStatus: null,
        assignedByRoster: false,
        assignedAt: null,
        assignedBy: null,
      },
    });
  };

  const setDutySlotStatus = ({ event, role, slot, status }) => {
    updateDutySlot({
      event,
      roleId: role.id,
      slotId: slot.id,
      patch: { responseStatus: status || null },
    });
  };

  const removeDutySlot = async ({ event, role, slot }) => {
    if (!window.confirm(`Remove this ${role.name || 'duty'} slot from ${event.name || 'this event'}?`)) return;
    const nextRoles = (Array.isArray(event.roles) ? event.roles : []).reduce((acc, existingRole) => {
      if (existingRole.id !== role.id) {
        acc.push(existingRole);
        return acc;
      }
      const nextSlots = (Array.isArray(existingRole.slots) ? existingRole.slots : []).filter((existingSlot) => existingSlot.id !== slot.id);
      if (nextSlots.length === 0 && existingRole.dutyRoster === true) return acc;
      acc.push({ ...existingRole, slots: nextSlots });
      return acc;
    }, []);
    setBusy(true);
    setError('');
    try {
      await setDoc(doc(db, 'teams', teamId, 'events', event.id), {
        roles: nextRoles,
        updatedAt: new Date(),
        updatedBy: userId,
      }, { merge: true });
    } catch (err) {
      setError(err?.message || 'Unable to remove duty slot.');
    } finally {
      setBusy(false);
    }
  };

  const removeDutyRole = async ({ event, role }) => {
    if (!window.confirm(`Remove ${role.name || 'this duty role'} and all of its slots from ${event.name || 'this event'}?`)) return;
    const nextRoles = (Array.isArray(event.roles) ? event.roles : []).filter((existingRole) => existingRole.id !== role.id);
    setBusy(true);
    setError('');
    try {
      await setDoc(doc(db, 'teams', teamId, 'events', event.id), {
        roles: nextRoles,
        updatedAt: new Date(),
        updatedBy: userId,
      }, { merge: true });
    } catch (err) {
      setError(err?.message || 'Unable to remove duty role.');
    } finally {
      setBusy(false);
    }
  };

  const patchTemplateRole = (roleId, patch) => {
    setNewTemplate((prev) => {
      const roles = (prev.roles || []).map((role) =>
        role.id === roleId ? { ...role, ...patch } : role
      );
      return { ...prev, roles };
    });
  };

  const addTemplateRole = () => {
    setNewTemplate((prev) => ({
      ...prev,
      roles: [...(prev.roles || []), createTemplateRole({ name: '' })],
    }));
  };

  const removeTemplateRole = (roleId) => {
    setNewTemplate((prev) => ({
      ...prev,
      roles: (prev.roles || []).length <= 1
        ? prev.roles
        : (prev.roles || []).filter((role) => role.id !== roleId),
    }));
  };

  const resetTemplateForm = () => {
    setEditingTemplateId('');
    setNewTemplate({
      name: '',
      roles: [createTemplateRole()],
    });
  };

  const loadTemplateForEdit = (template) => {
    const roles = (Array.isArray(template.roles) && template.roles.length ? template.roles : [])
      .map((role) => createTemplateRole({
        name: role.name || '',
        slots: role.slots || role.defaultSlots || 1,
        dutyCredit: role.dutyCredit ?? 1,
        allowedTags: normalizeTags(role.allowedTags),
        requiresApproval: role.requiresApproval !== false,
      }));
    setEditingTemplateId(template.id);
    setNewTemplate({
      name: template.name || '',
      roles: roles.length ? roles : [createTemplateRole({ name: '' })],
    });
    setError('');
  };

  const handleExistingTemplateSelect = (templateDocId) => {
    const nextId = cleanText(templateDocId);
    if (!nextId) {
      resetTemplateForm();
      return;
    }
    const template = templates.find((entry) => entry.id === nextId);
    if (template) loadTemplateForEdit(template);
  };

  const deleteTemplate = async (template) => {
    if (!template?.id || !window.confirm(`Delete ${template.name || 'this template'}?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'teams', teamId, 'dutyRosterTemplates', template.id));
      if (editingTemplateId === template.id) resetTemplateForm();
      if (templateId === template.id) setTemplateId('');
    } catch (err) {
      setError(err?.message || 'Unable to delete template.');
    } finally {
      setBusy(false);
    }
  };

  const handleTemplateTagToggle = (roleId, tagName) => {
    setNewTemplate((prev) => {
      const roles = (prev.roles || []).map((role) => {
        if (role.id !== roleId) return role;
        const next = new Set(role.allowedTags || []);
        if (next.has(tagName)) next.delete(tagName);
        else next.add(tagName);
        return { ...role, allowedTags: Array.from(next) };
      });
      return { ...prev, roles };
    });
  };

  const saveTemplate = async () => {
    const name = cleanText(newTemplate.name);
    const roles = (newTemplate.roles || [])
      .map((role) => ({
        name: cleanText(role.name),
        slots: positiveInteger(role.slots, 1),
        allowedTags: role.allowedTags || [],
        requiresApproval: role.requiresApproval !== false,
        dutyCredit: nonNegativeNumber(role.dutyCredit, 1),
      }))
      .filter((role) => role.name);
    if (!name || roles.length === 0) {
      setError('Template name and at least one role name are required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const now = new Date();
      const templateRef = doc(db, 'teams', teamId, 'dutyRosterTemplates', editingTemplateId || crypto.randomUUID());
      await setDoc(templateRef, {
        name,
        roles,
        ...(editingTemplateId ? {} : { createdAt: now, createdBy: userId }),
        updatedAt: now,
        updatedBy: userId,
      }, { merge: Boolean(editingTemplateId) });
      resetTemplateForm();
    } catch (err) {
      setError(err?.message || 'Unable to save template.');
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = async () => {
    if (!templateId || selectedEventIds.length === 0) {
      setError('Choose a template and at least one event.');
      return;
    }
    const selectedEventsWithRoles = selectedEventIds
      .map((eventId) => events.find((event) => event.id === eventId))
      .filter((event) => event && dutyRoles(event).length > 0);
    if (selectedEventsWithRoles.length > 0) {
      const eventNames = selectedEventsWithRoles.slice(0, 5).map((event) => event.name || 'Event').join(', ');
      const extraCount = selectedEventsWithRoles.length - 5;
      const suffix = extraCount > 0 ? ` and ${extraCount} more` : '';
      const confirmed = window.confirm(
        `${selectedEventsWithRoles.length} selected event${selectedEventsWithRoles.length === 1 ? ' already has' : 's already have'} duty roles: ${eventNames}${suffix}.\n\nApplying this template will add more duty roles to those events. Continue?`
      );
      if (!confirmed) return;
    }
    setBusy(true);
    setError('');
    try {
      const targets = selectedEventIds.map((eventId) => ({
        eventId,
        occurrenceId: cleanText(occurrenceByEvent[eventId]),
      }));
      await applyTemplateFn({ teamId, templateId, targets });
      setSelectedEventIds([]);
    } catch (err) {
      setError(err?.message || 'Unable to apply template.');
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    if (selectedEventIds.length === 0) {
      setError('Select events with duty slots before creating a draft.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await createDraftFn({
        teamId,
        eventIds: selectedEventIds,
        seasonId: 'current',
        rsvpMode: draftRsvpMode,
      });
      setDraftId(result.data?.draftId || '');
      setDraftSlots(result.data?.slots || []);
      setTab('draft');
    } catch (err) {
      setError(err?.message || 'Unable to create draft roster.');
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    if (!draftId || draftSlots.length === 0) return;
    setBusy(true);
    setError('');
    try {
      await publishDraftFn({ teamId, draftId, slots: draftSlots });
      setDraftSlots([]);
      setDraftId('');
      setSelectedEventIds([]);
      setTab('grid');
    } catch (err) {
      setError(err?.message || 'Unable to publish roster.');
    } finally {
      setBusy(false);
    }
  };

  const respondDuty = async ({ event, role, slot, status }) => {
    setBusy(true);
    setError('');
    try {
      await respondDutyFn({
        teamId,
        eventId: event.id,
        roleId: role.id,
        slotId: slot.id,
        status,
      });
    } catch (err) {
      setError(err?.message || 'Unable to update duty response.');
    } finally {
      setBusy(false);
    }
  };

  const requestSwap = async ({ event, role, slot }) => {
    setBusy(true);
    setError('');
    try {
      await createSwapFn({
        teamId,
        eventId: event.id,
        roleId: role.id,
        slotId: slot.id,
      });
      setTab('swaps');
    } catch (err) {
      setError(err?.message || 'Unable to create swap request.');
    } finally {
      setBusy(false);
    }
  };

  const resolveSwap = async (swapRequestId, action, acceptingMemberId = '', dutyCreditValue = '') => {
    setBusy(true);
    setError('');
    try {
      const payload = { teamId, swapRequestId, action, acceptingMemberId };
      if (dutyCreditValue !== '') payload.dutyCredit = Number(dutyCreditValue);
      await resolveSwapFn(payload);
    } catch (err) {
      setError(err?.message || 'Unable to update swap request.');
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    setError('');
    try {
      await saveSettingsFn({
        teamId,
        season: {
          seasonStartsOn: parseSeasonStartsOn(season.seasonStartsOn),
        },
        requiresAdminApprovalForSwaps: swapApprovalRequired,
      });
    } catch (err) {
      setError(err?.message || 'Unable to save roster settings.');
    } finally {
      setBusy(false);
    }
  };

  const rosterSummary = useMemo(() => events.reduce((acc, event) => {
    const summary = summarizeEventDuties(event);
    acc.total += summary.total;
    acc.filled += summary.filled;
    acc.warnings += summary.warning;
    acc.pending += summary.pending;
    return acc;
  }, { total: 0, filled: 0, warnings: 0, pending: 0 }), [events]);

  const openSwapCount = swapRequests.filter((swap) =>
    ['open', 'pendingAdmin'].includes(String(swap.status || 'open'))
  ).length;
  const currentMember = members.find((member) => member.id === userId) || null;
  const memberCanOfferSwap = (swap) => {
    if (!swap || swap.requestedBy === userId) return false;
    if (Array.isArray(swap.eligibleMemberIds)) return swap.eligibleMemberIds.includes(userId);
    return hasTagOverlap(swap.allowedTags, currentMember?.tags || []);
  };
  const visibleSwapRequests = isAdmin
    ? swapRequests
    : swapRequests.filter((swap) =>
        swap.requestedBy === userId ||
        swap.acceptingMemberId === userId ||
        (String(swap.status || 'open') === 'open' && memberCanOfferSwap(swap))
      );
  const visibleOpenSwapCount = visibleSwapRequests.filter((swap) =>
    ['open', 'pendingAdmin'].includes(String(swap.status || 'open'))
  ).length;
  const myOpenSwapCount = visibleSwapRequests.filter((swap) =>
    ['open', 'pendingAdmin'].includes(String(swap.status || 'open')) &&
    (swap.requestedBy === userId || swap.acceptingMemberId === userId || memberCanOfferSwap(swap))
  ).length;
  const myPendingDuties = myDuties.filter((entry) =>
    String(entry.slot?.responseStatus || '').toLowerCase() === 'pending'
  ).length;
  const defaultSwapCreditFor = (swap) => {
    const event = events.find((candidate) => candidate.id === swap.eventId);
    const role = dutyRoles(event || {}).find((candidate) => candidate.id === swap.roleId);
    const slot = (Array.isArray(role?.slots) ? role.slots : []).find((candidate) => candidate.id === swap.slotId);
    return dutyCredit(role, slot);
  };
  const displayMemberName = (memberId, storedName = '') => {
    if (!isGenericMemberName(storedName)) return storedName;
    const overrideName = swapMemberNames[memberId];
    if (!isGenericMemberName(overrideName)) return overrideName;
    const localName = memberNameById.get(memberId);
    if (!isGenericMemberName(localName)) return localName;
    return 'a member';
  };

  const renderMyDutiesSection = ({ compact = false } = {}) => (
    <section className="duty-roster-section">
      <div className="duty-roster-section__head">
        <div>
          <h3>My Duties</h3>
          <div className="duty-roster-section__subtitle">
            Completed this season: {formatDutyCredit(mySeasonStats.completedCredits)}
          </div>
        </div>
        {myPendingDuties > 0 && (
          <span className="duty-roster-pill duty-roster-pill--danger">
            {myPendingDuties} need response
          </span>
        )}
      </div>
      <div className="duty-roster-section__body">
        <div className="duty-roster-status-strip">
          <span className="duty-roster-pill">{formatDutyCredit(mySeasonStats.completedCredits)} completed</span>
          <span className="duty-roster-pill">{mySeasonStats.futureDuties || 0} future duties</span>
          <span className="duty-roster-pill">Last completed: {mySeasonStats.lastCompletedLabel || 'None'}</span>
        </div>
        {myDuties.length === 0 ? (
          <p className="duty-roster-empty">No duty assignments.</p>
        ) : (
          <div className="duty-roster-list">
            {myDuties.slice(0, compact ? 4 : myDuties.length).map(({ event, role, slot }) => (
              <div key={`${event.id}:${role.id}:${slot.id}`} className="duty-roster-row">
                <div>
                  <div className="duty-roster-row__title">{event.name || 'Event'}</div>
                  <div className="duty-roster-row__meta">
                    {formatDateTime(event)} • {role.name || 'Duty'} • {statusLabel(slot.responseStatus)}
                  </div>
                </div>
                <div className="duty-roster-row__actions">
                  {slot.responseStatus !== 'accepted' && (
                    <button type="button" className="button button--xs" onClick={() => respondDuty({ event, role, slot, status: 'accepted' })}>
                      Accept
                    </button>
                  )}
                  {slot.responseStatus !== 'declined' && (
                    <button type="button" className="button button--xs" onClick={() => respondDuty({ event, role, slot, status: 'declined' })}>
                      Decline
                    </button>
                  )}
                  <button type="button" className="button button--xs" onClick={() => requestSwap({ event, role, slot })}>
                    Request swap
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {compact && myDuties.length > 4 && (
          <button type="button" className="button button-secondary button--xs" onClick={() => setTab('mine')}>
            View all duties
          </button>
        )}
        {!compact && (
          <div className="duty-roster-completed">
            <h4>Completed This Season</h4>
            {mySeasonStats.completedRows.length === 0 ? (
              <p className="duty-roster-empty">No completed duty credits in this season yet.</p>
            ) : (
              <div className="duty-roster-list">
                {mySeasonStats.completedRows.map(({ event, role, slot, credit }) => (
                  <div key={`completed:${event.id}:${role.id}:${slot.id}`} className="duty-roster-row">
                    <div>
                      <div className="duty-roster-row__title">{event.name || 'Event'}</div>
                      <div className="duty-roster-row__meta">
                        {formatDateTime(event)} • {role.name || 'Duty'} • {formatDutyCredit(credit)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );

  const renderFairnessExplanation = ({ compact = false } = {}) => (
    <section className="duty-roster-section duty-roster-section--explain">
      <div className="duty-roster-section__head">
        <div>
          <h3>How Fairness Works</h3>
          <div className="duty-roster-section__subtitle">
            Duty credits are the seasonal balance used when creating draft rosters.
          </div>
        </div>
      </div>
      <div className="duty-roster-section__body">
        <div className="duty-roster-explainer-grid">
          <div>
            <h4>Duty credits</h4>
            <p>
              Each role has a credit value. Credits count after the event date for the member assigned at that time, unless the duty was declined.
            </p>
          </div>
          <div>
            <h4>Fairness draft</h4>
            <p>
              Drafts filter active eligible members by tags and timing conflicts, then prefer fewer completed credits, fewer future duties, and the least recent duty.
            </p>
          </div>
          {!compact && isAdmin && (
            <div>
              <h4>Swaps</h4>
              <p>
                Pending swap offers do not move credit. An approved swap moves the assignment, so the replacement receives the completed credit.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  if (!isOpen) return null;

  const tabs = isAdmin
    ? [
        ['overview', 'Overview'],
        ['grid', 'Roster'],
        ['templates', 'Templates'],
        ['draft', 'Fairness Draft'],
        ['mine', 'My Duties'],
        ['swaps', 'Swaps'],
        ['settings', 'Settings'],
      ]
    : [
        ['overview', 'Overview'],
        ['mine', 'My Duties'],
        ['swaps', 'Swaps'],
      ];

  return (
    <div className="modal-form duty-roster-modal">
      <header className="duty-roster-modal__header">
        <div className="duty-roster-modal__title-row">
          <div>
            <h2 className="duty-roster-modal__title">Duty Roster</h2>
            <p className="duty-roster-modal__subtitle">
              Event role duties, responses, swaps, and fairness-balanced assignment.
            </p>
          </div>
          <button type="button" className="button button-secondary duty-roster-modal__close" onClick={onClose}>Close</button>
        </div>

        <div className="duty-roster-modal__summary" aria-label="Duty roster summary">
          <div className="duty-roster-metric">
            <span className="duty-roster-metric__label">My Duties</span>
            <span className="duty-roster-metric__value">{myDuties.length}</span>
          </div>
          <div className={`duty-roster-metric ${myPendingDuties > 0 ? 'duty-roster-metric--attention' : ''}`}>
            <span className="duty-roster-metric__label">Needs Response</span>
            <span className="duty-roster-metric__value">{myPendingDuties}</span>
          </div>
          {isAdmin ? (
            <div className={`duty-roster-metric ${rosterSummary.warnings > 0 ? 'duty-roster-metric--attention' : ''}`}>
              <span className="duty-roster-metric__label">Roster Warnings</span>
              <span className="duty-roster-metric__value">{rosterSummary.warnings}</span>
            </div>
          ) : (
            <div className="duty-roster-metric">
              <span className="duty-roster-metric__label">Completed</span>
              <span className="duty-roster-metric__value">{seasonDutyCounts[userId] || 0}</span>
            </div>
          )}
          <div className={`duty-roster-metric ${(isAdmin ? openSwapCount : visibleOpenSwapCount) > 0 ? 'duty-roster-metric--attention' : ''}`}>
            <span className="duty-roster-metric__label">{isAdmin ? 'Open Swaps' : 'Available Swaps'}</span>
            <span className="duty-roster-metric__value">{isAdmin ? openSwapCount : myOpenSwapCount}</span>
          </div>
        </div>

        <div className="duty-roster-modal__tabs" role="tablist" aria-label="Duty roster sections">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className="duty-roster-tab"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="duty-roster-modal__body">
        {error && <p className="modal-error">{error}</p>}
        {busy && <p className="text-muted" role="status">Updating roster...</p>}

        {tab === 'overview' && (
          <div className="duty-roster-panel">
            {renderFairnessExplanation({ compact: !isAdmin })}
            <div className="duty-roster-overview-grid">
              {renderMyDutiesSection({ compact: true })}
              <section className="duty-roster-section">
                <div className="duty-roster-section__head">
                  <div>
                    <h3>{isAdmin ? 'Roster Attention' : 'Swap Tickets'}</h3>
                    <div className="duty-roster-section__subtitle">
                      {isAdmin ? 'Unfilled, declined, and pending duty slots.' : 'Open swap requests you can act on.'}
                    </div>
                  </div>
                </div>
                <div className="duty-roster-section__body">
                  <div className="duty-roster-status-strip">
                    {isAdmin && <span className={`duty-roster-pill ${rosterSummary.warnings > 0 ? 'duty-roster-pill--danger' : ''}`}>{rosterSummary.warnings} warnings</span>}
                    {isAdmin && <span className="duty-roster-pill">{rosterSummary.pending} pending responses</span>}
                    <span className={`duty-roster-pill ${(isAdmin ? openSwapCount : visibleOpenSwapCount) > 0 ? 'duty-roster-pill--danger' : ''}`}>{isAdmin ? openSwapCount : visibleOpenSwapCount} open swaps</span>
                  </div>
                  {isAdmin ? (
                    <div className="duty-roster-row__actions">
                      <button type="button" className="button button--xs" onClick={() => setTab('grid')}>Open roster grid</button>
                      <button type="button" className="button button--xs" onClick={() => setTab('draft')}>Review fairness draft</button>
                      <button type="button" className="button button--xs" onClick={() => setTab('swaps')}>Manage swaps</button>
                    </div>
                  ) : (
                    <div className="duty-roster-row__actions">
                      <button type="button" className="button button--xs" onClick={() => setTab('swaps')}>View swaps</button>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}

      {tab === 'grid' && isAdmin && (
        <section className="duty-roster-section">
          <div className="duty-roster-section__head">
            <div>
              <h3>Roster Grid</h3>
              <div className="duty-roster-section__subtitle">Select events to generate duty roles or create a fairness draft.</div>
            </div>
          </div>
          <div className="duty-roster-section__body">
            <div className="text-muted">
              Season: {season.name || 'Current season'} • {season.startDate || currentYearSeason().startDate} to {season.endDate || currentYearSeason().endDate}
            </div>
            {Object.keys(seasonDutyCounts).length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(seasonDutyCounts)
                  .sort((left, right) => left[1] - right[1])
                  .slice(0, 8)
                  .map(([memberId, count]) => (
                    <span key={memberId} className="status-pill info">
                      {members.find((member) => member.id === memberId)?.name || 'Member'}: {count}
                    </span>
                  ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} style={{ minWidth: 220 }}>
                <option value="">Select template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name || 'Template'}</option>
                ))}
              </select>
              <select className="input" value={draftRsvpMode} onChange={(e) => setDraftRsvpMode(e.target.value)} style={{ minWidth: 220 }}>
                <option value="all">Draft from all eligible members</option>
                <option value="yes">Draft from RSVP yes members</option>
              </select>
              <button type="button" className="button button--xs" onClick={applyTemplate} disabled={busy}>Apply template</button>
              <button type="button" className="button button--xs" onClick={createDraft} disabled={busy}>Create fairness draft</button>
            </div>

            {futureEvents.length === 0 ? (
              <p className="text-muted">No upcoming events found.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {futureEvents.map((event) => {
                  const summary = summarizeEventDuties(event);
                  const roles = dutyRoles(event);
                  const isExpanded = expandedRosterEventIds.includes(event.id);
                  const isRecurring = String(event.type || '').toLowerCase() === 'recurring' || Boolean(event.recurrence?.frequency);
                  return (
                    <div key={event.id} className="duty-roster-row">
                      <div className="duty-roster-row--selectable">
                        <input type="checkbox" checked={selectedEventIds.includes(event.id)} onChange={() => toggleSelectedEvent(event.id)} />
                        <div>
                          <div className="duty-roster-row__actions" style={{ justifyContent: 'space-between' }}>
                            <div>
                              <div className="duty-roster-row__title">{event.name || 'Event'}</div>
                              <div className="duty-roster-row__meta">{formatDateTime(event)}</div>
                              <div className={summary.warning > 0 ? 'duty-roster-row__warning' : 'duty-roster-row__meta'}>
                                {summary.total} duty slot{summary.total === 1 ? '' : 's'} • {summary.filled} filled • {summary.warning} warning{summary.warning === 1 ? '' : 's'} • {summary.pending} pending
                              </div>
                            </div>
                            <button type="button" className="button button-secondary button--xs" onClick={() => toggleExpandedRosterEvent(event.id)}>
                              {isExpanded ? 'Hide roles' : 'Edit roles'}
                            </button>
                          </div>
                        </div>
                      </div>
                      {isRecurring && selectedEventIds.includes(event.id) && (
                        <label className="form-field" style={{ maxWidth: 220 }}>
                          <span className="field-label">Occurrence date</span>
                          <input
                            className="input"
                            type="date"
                            value={occurrenceByEvent[event.id] || ''}
                            onChange={(e) => setOccurrenceByEvent((prev) => ({ ...prev, [event.id]: e.target.value }))}
                          />
                        </label>
                      )}
                      {isExpanded && (
                        <div className="duty-roster-role-editor">
                          {roles.length === 0 ? (
                            <p className="duty-roster-empty">No duty roles yet. Apply a template to add slots.</p>
                          ) : roles.map((role) => (
                            <div key={role.id} className="duty-roster-role-editor__role">
                              <div className="duty-roster-row__actions" style={{ justifyContent: 'space-between' }}>
                                <div>
                                  <div className="duty-roster-row__title">{role.name || 'Duty'}</div>
                                  <div className="duty-roster-row__meta">
                                    {formatDutyCredit(role.dutyCredit)} per completed slot
                                    {(role.allowedTags || []).length ? ` • ${(role.allowedTags || []).join(', ')}` : ''}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="button button-secondary button--xs"
                                  onClick={() => removeDutyRole({ event, role })}
                                  disabled={busy}
                                >
                                  Delete role
                                </button>
                              </div>
                              <div className="duty-roster-list">
                                {(Array.isArray(role.slots) ? role.slots : []).map((slot, slotIndex) => (
                                  <div key={slot.id || `${role.id}-${slotIndex}`} className="duty-roster-slot-editor">
                                    <div>
                                      <div className="duty-roster-row__title">Slot {slotIndex + 1}</div>
                                      <div className="duty-roster-row__meta">
                                        {slot.assigneeDisplayName || members.find((member) => member.id === slot.assigneeUserId)?.name || 'Unfilled'} • {statusLabel(slot.responseStatus)}
                                      </div>
                                    </div>
                                    <select
                                      className="input"
                                      value={slot.assigneeUserId || ''}
                                      onChange={(e) => assignDutySlot({ event, role, slot, memberId: e.target.value })}
                                    >
                                      <option value="">Unfilled</option>
                                      {members.map((member) => (
                                        <option key={member.id} value={member.id}>{member.name}</option>
                                      ))}
                                    </select>
                                    <select
                                      className="input"
                                      value={slot.responseStatus || ''}
                                      onChange={(e) => setDutySlotStatus({ event, role, slot, status: e.target.value })}
                                      disabled={!slot.assigneeUserId}
                                    >
                                      <option value="">Assigned</option>
                                      <option value="pending">Needs response</option>
                                      <option value="accepted">Accepted</option>
                                      <option value="declined">Declined</option>
                                    </select>
                                    <button
                                      type="button"
                                      className="button button-secondary button--xs"
                                      onClick={() => removeDutySlot({ event, role, slot })}
                                      disabled={busy}
                                    >
                                      Delete slot
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'templates' && isAdmin && (
        <section className="duty-roster-section">
          <div className="duty-roster-section__head">
            <div>
              <h3>Duty Templates</h3>
              <div className="duty-roster-section__subtitle">Templates bulk-add multiple duty role slots to existing events.</div>
            </div>
          </div>
          <div className="duty-roster-section__body">
            <div className="duty-roster-note">
              Duty credit is the fairness weight for each completed slot. Use higher credits for heavier duties and zero for roles that should not affect the seasonal balance.
            </div>
            <div className="form-grid" style={{ marginBottom: 4 }}>
              <label className="form-field">
                <span className="field-label">Existing template</span>
                <select className="input" value={editingTemplateId} onChange={(e) => handleExistingTemplateSelect(e.target.value)}>
                  <option value="">New template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>{template.name || 'Template'}</option>
                  ))}
                </select>
              </label>
              {editingTemplateId && (
                <div className="form-field">
                  <span className="field-label">&nbsp;</span>
                  <button type="button" className="button button-secondary button--xs" onClick={resetTemplateForm} disabled={busy}>
                    Start new template
                  </button>
                </div>
              )}
            </div>
            <div className="form-grid" style={{ marginBottom: 4 }}>
              <label className="form-field">
                <span className="field-label">{editingTemplateId ? 'Editing template' : 'Template name'}</span>
                <input className="input" value={newTemplate.name} onChange={(e) => setNewTemplate((prev) => ({ ...prev, name: e.target.value }))} />
              </label>
            </div>

            <div className="duty-roster-list">
              {(newTemplate.roles || []).map((role, roleIndex) => (
                <div key={role.id} className="duty-roster-row">
                  <div className="duty-roster-row__actions" style={{ justifyContent: 'space-between' }}>
                    <div className="duty-roster-row__title">Role {roleIndex + 1}</div>
                    {(newTemplate.roles || []).length > 1 && (
                      <button type="button" className="button button-secondary button--xs" onClick={() => removeTemplateRole(role.id)}>
                        Remove role
                      </button>
                    )}
                  </div>
                  <div className="form-grid">
                    <label className="form-field">
                      <span className="field-label">Role name</span>
                      <input className="input" value={role.name || ''} onChange={(e) => patchTemplateRole(role.id, { name: e.target.value })} />
                    </label>
                    <label className="form-field">
                      <span className="field-label">Slots</span>
                      <input className="input" type="number" min="1" value={role.slots} onChange={(e) => patchTemplateRole(role.id, { slots: e.target.value })} />
                    </label>
                    <label className="form-field">
                      <span className="field-label">Duty credit</span>
                      <input className="input" type="number" min="0" step="0.5" value={role.dutyCredit} onChange={(e) => patchTemplateRole(role.id, { dutyCredit: e.target.value })} />
                    </label>
                  </div>
                  <div className="checkbox-row">
                    <input
                      id={`duty-template-approval-${role.id}`}
                      type="checkbox"
                      checked={role.requiresApproval !== false}
                      onChange={(e) => patchTemplateRole(role.id, { requiresApproval: e.target.checked })}
                    />
                    <label htmlFor={`duty-template-approval-${role.id}`}>Require admin approval for volunteers</label>
                  </div>
                  <div>
                    <div className="text-muted" style={{ marginBottom: 6 }}>Eligible tags / qualifications</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {tagNames.length === 0 ? <span className="text-muted">No tags available.</span> : tagNames.map((tagName) => (
                        <label key={`${role.id}:${tagName}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <input type="checkbox" checked={(role.allowedTags || []).includes(tagName)} onChange={() => handleTemplateTagToggle(role.id, tagName)} />
                          {tagName}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <div className="duty-roster-row__actions">
                <button type="button" className="button button-secondary button--xs" onClick={addTemplateRole}>
                  Add another role
                </button>
                <button type="button" className="button button--xs" onClick={saveTemplate} disabled={busy}>
                  {editingTemplateId ? 'Update template' : 'Save template'}
                </button>
                {editingTemplateId && (
                  <button type="button" className="button button-secondary button--xs" onClick={resetTemplateForm} disabled={busy}>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
              {templates.length > 0 && <div className="text-muted">Saved templates</div>}
              {templates.map((template) => (
                <div key={template.id} className="duty-roster-row">
                  <div className="duty-roster-row__actions" style={{ justifyContent: 'space-between' }}>
                    <div>
                      <div className="duty-roster-row__title">{template.name || 'Template'}</div>
                      <div className="duty-roster-row__meta">
                        {(template.roles || []).map((role) => `${role.name || 'Duty'} (${role.slots || role.defaultSlots || 1} slots, ${formatDutyCredit(role.dutyCredit)})`).join(', ') || 'No roles'}
                      </div>
                    </div>
                    <div className="duty-roster-row__actions">
                      <button type="button" className="button button-secondary button--xs" onClick={() => loadTemplateForEdit(template)} disabled={busy}>
                        Select
                      </button>
                      <button type="button" className="button button-secondary button--xs" onClick={() => deleteTemplate(template)} disabled={busy}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {tab === 'draft' && isAdmin && (
        <section className="card">
          <div className="card-header">
            <h3 style={{ margin: 0 }}>Fairness Draft</h3>
            <div className="card-subtitle">Review suggestions before publishing pending duty assignments.</div>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: 10 }}>
            <div className="duty-roster-note">
              The draft is ordered by eligible members with the lowest completed season credits first, then fewer future duties, then least recent duty. Use the roster grid option to draft from all eligible members or only members who RSVP'd yes to each RSVP event.
            </div>
            <section className="duty-roster-section">
              <div className="duty-roster-section__head">
                <div>
                  <h3>Season Credit Balance</h3>
                  <div className="duty-roster-section__subtitle">
                    Credits are awarded after the event to the member assigned at that time, unless the duty was declined.
                  </div>
                </div>
              </div>
              <div className="duty-roster-section__body">
                <div className="duty-roster-toolbar duty-roster-toolbar--between">
                  <label className="form-field duty-roster-search-field">
                    <span className="field-label">Search members</span>
                    <input
                      className="input"
                      type="search"
                      value={creditBalanceSearch}
                      onChange={(e) => {
                        setCreditBalanceSearch(e.target.value);
                        setShowAllCreditBalance(false);
                      }}
                      placeholder="Search by name"
                    />
                  </label>
                  <div className="duty-roster-row__actions">
                    <span className="text-muted">
                      Showing {visibleCreditBalance.length} of {filteredCreditBalance.length}
                    </span>
                    {creditBalanceSearch && (
                      <button type="button" className="button button-secondary button--xs" onClick={() => setCreditBalanceSearch('')}>
                        Clear
                      </button>
                    )}
                    {!creditBalanceSearch && hiddenCreditBalanceCount > 0 && (
                      <button type="button" className="button button-secondary button--xs" onClick={() => setShowAllCreditBalance(true)}>
                        Show all
                      </button>
                    )}
                    {!creditBalanceSearch && showAllCreditBalance && seasonCreditBalance.length > CREDIT_BALANCE_LIMIT && (
                      <button type="button" className="button button-secondary button--xs" onClick={() => setShowAllCreditBalance(false)}>
                        Show fewer
                      </button>
                    )}
                  </div>
                </div>
                <div className="duty-roster-table-wrap">
                  <table className="duty-roster-table">
                    <thead>
                      <tr>
                        <th scope="col">Member</th>
                        <th scope="col">Completed credits</th>
                        <th scope="col">Future duties</th>
                        <th scope="col">Last completed duty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCreditBalance.map((entry) => (
                        <tr key={entry.memberId}>
                          <td>{entry.name}</td>
                          <td>{formatDutyCredit(entry.completedCredits)}</td>
                          <td>{entry.futureDuties}</td>
                          <td>{entry.lastCompletedLabel}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredCreditBalance.length === 0 && (
                  <p className="duty-roster-empty">No members match that search.</p>
                )}
                {!creditBalanceSearch && hiddenCreditBalanceCount > 0 && (
                  <p className="text-muted">
                    {hiddenCreditBalanceCount} more members hidden. Search by name or show all to review the full balance.
                  </p>
                )}
              </div>
            </section>
            {draftSlots.length === 0 ? (
              <p className="text-muted">Create a draft from selected roster events.</p>
            ) : (
              <>
                {draftSlots.map((slot, index) => (
                  <div key={`${slot.eventId}:${slot.roleId}:${slot.slotId}`} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, display: 'grid', gap: 6 }}>
                    <div>
                      <strong>{slot.eventName || 'Event'}</strong>
                      <div className="text-muted">
                        {slot.roleName || 'Duty'} • {slot.candidateCount || 0} eligible{slot.rsvpMode === 'yes' ? ' from RSVP yes' : ''}
                      </div>
                    </div>
                    <select
                      className="input"
                      value={slot.suggestedMemberId || ''}
                      onChange={(e) => {
                        const selected = members.find((member) => member.id === e.target.value);
                        setDraftSlots((prev) => prev.map((entry, entryIndex) => entryIndex === index ? {
                          ...entry,
                          suggestedMemberId: selected?.id || '',
                          suggestedDisplayName: selected?.name || '',
                        } : entry));
                      }}
                    >
                      <option value="">Leave unfilled</option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>{member.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <button type="button" className="button button--xs" onClick={publishDraft} disabled={busy}>Publish draft</button>
              </>
            )}
          </div>
        </section>
      )}

      {tab === 'mine' && renderMyDutiesSection()}

      {tab === 'swaps' && (
        <section className="duty-roster-section">
          <div className="duty-roster-section__head">
            <div>
              <h3>Swap Tickets</h3>
              <div className="duty-roster-section__subtitle">Club setting controls whether accepted swaps need admin approval.</div>
            </div>
          </div>
          <div className="duty-roster-section__body">
            {visibleSwapRequests.length === 0 ? (
              <p className="duty-roster-empty">No swap tickets.</p>
            ) : visibleSwapRequests.map((swap) => {
              const status = String(swap.status || 'open');
              const hasOffer = Boolean(swap.acceptingMemberId);
              const selectedReplacementId = swap.acceptingMemberId || '';
              const creditValue = swapCreditOverrides[swap.id] ?? String(defaultSwapCreditFor(swap));
              return (
                <div key={swap.id} className="duty-roster-row">
                  <div>
                    <div className="duty-roster-row__actions" style={{ justifyContent: 'space-between' }}>
                      <div>
                        <div className="duty-roster-row__title">{swap.eventName || 'Event'}</div>
                        <div className="duty-roster-row__meta">
                          {swap.roleName || 'Duty'} • requested by {displayMemberName(swap.requestedBy, swap.requestedByName)} • {swapStatusLabel(status)}
                        </div>
                        {hasOffer && (
                          <div className="duty-roster-row__meta">
                            Offered by {displayMemberName(swap.acceptingMemberId, swap.acceptingMemberName)}
                          </div>
                        )}
                      </div>
                      <span className={`duty-roster-pill ${status === 'pendingAdmin' ? 'duty-roster-pill--danger' : ''}`}>
                        {swapStatusLabel(status)}
                      </span>
                    </div>
                  </div>
                  <div className="duty-roster-row__actions">
                    {!isAdmin && status === 'open' && memberCanOfferSwap(swap) && (
                      <button type="button" className="button button--xs" onClick={() => resolveSwap(swap.id, 'accept')}>Offer to swap</button>
                    )}
                    {isAdmin && ['open', 'pendingAdmin'].includes(status) && (
                      <>
                        {hasOffer ? (
                          <span className="text-muted">Replacement: {displayMemberName(swap.acceptingMemberId, swap.acceptingMemberName)}</span>
                        ) : (
                          <select className="input" defaultValue={selectedReplacementId} onChange={(e) => setSwapRequests((prev) => prev.map((entry) => entry.id === swap.id ? { ...entry, acceptingMemberId: e.target.value, acceptingMemberName: members.find((member) => member.id === e.target.value)?.name || '' } : entry))} style={{ minWidth: 180 }}>
                            <option value="">Select replacement</option>
                            {members
                              .filter((member) => !Array.isArray(swap.eligibleMemberIds) || swap.eligibleMemberIds.includes(member.id))
                              .map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                          </select>
                        )}
                        <label className="form-field" style={{ maxWidth: 150 }}>
                          <span className="field-label">Duty credit</span>
                          <input
                            className="input"
                            type="number"
                            min="0"
                            step="0.5"
                            value={creditValue}
                            onChange={(e) => setSwapCreditOverrides((prev) => ({ ...prev, [swap.id]: e.target.value }))}
                          />
                        </label>
                        <button type="button" className="button button--xs" onClick={() => resolveSwap(swap.id, 'approve', selectedReplacementId, creditValue)} disabled={!selectedReplacementId}>
                          {status === 'pendingAdmin' ? 'Approve offer' : 'Approve'}
                        </button>
                        <button type="button" className="button button--xs" onClick={() => resolveSwap(swap.id, 'reject')}>Reject</button>
                      </>
                    )}
                    {(isAdmin || swap.requestedBy === userId) && ['open', 'pendingAdmin'].includes(status) && (
                      <button type="button" className="button button--xs" onClick={() => resolveSwap(swap.id, 'cancel')}>Cancel</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {tab === 'settings' && isAdmin && (
        <section className="duty-roster-section">
          <div className="duty-roster-section__head">
            <div>
              <h3>Roster Settings</h3>
              <div className="duty-roster-section__subtitle">Set the season used for duty counts and swap review.</div>
            </div>
          </div>
          <div className="duty-roster-section__body">
            <div className="duty-roster-note">
              Pick the day your duty season rolls over. The active season is generated automatically from that date.
            </div>
            <div className="form-grid">
              <label className="form-field">
                <span className="field-label">Rollover month</span>
                <select className="input" value={seasonStartParts.month} onChange={(e) => setSeasonStartPart('month', e.target.value)}>
                  {Array.from({ length: 12 }, (_, index) => {
                    const month = String(index + 1).padStart(2, '0');
                    return <option key={month} value={month}>{new Date(2000, index, 1).toLocaleString('en-GB', { month: 'long' })}</option>;
                  })}
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Rollover day</span>
                <select className="input" value={seasonStartParts.day} onChange={(e) => setSeasonStartPart('day', e.target.value)}>
                  {Array.from({ length: seasonStartDayCount }, (_, index) => {
                    const day = String(index + 1).padStart(2, '0');
                    return <option key={day} value={day}>{index + 1}</option>;
                  })}
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Current season</span>
                <input className="input" value={`${season.name || currentYearSeason().name}: ${season.startDate || currentYearSeason().startDate} to ${season.endDate || currentYearSeason().endDate}`} readOnly />
              </label>
            </div>
            <div className="checkbox-row">
              <input id="duty-swap-approval" type="checkbox" checked={swapApprovalRequired} onChange={(e) => setSwapApprovalRequired(e.target.checked)} />
              <label htmlFor="duty-swap-approval">Require admin approval for member swaps</label>
            </div>
            <button type="button" className="button button--xs" onClick={saveSettings} disabled={busy}>Save settings</button>
          </div>
        </section>
      )}
      </main>
    </div>
  );
}
